import { useEffect, useState, SyntheticEvent } from 'react';
import { Modal, Box } from '@material-ui/core';
import { ModalBody } from 'react-bootstrap';
import ModalHeader from './../components/Modal/ModalHeader';
import Button from 'react-bootstrap/Button';
import ReplacementsTable from '../components/Replacements/ReplacementsTable';
import ReplacementForm from '../components/Replacements/ReplacementForm';
import { ReplacementType, ReplacementTypeObject } from '../types/AppTypes';
import Content from '../components/Content';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import Skeleton from '../components/ui/Skeleton/Skeleton';
import { useSelector } from 'react-redux';
import { UserInfoType } from '../types/AppTypes';
import { RootState } from '../redux/store';
import { getReplacements, createUpdateReplacement, deleteReplacement } from '../utils/api';
import 'bootstrap/dist/css/bootstrap.min.css';

interface IReplacementsResponse {
  error: null | string;
  data: ReplacementType[];
}

const Replacements = (): JSX.Element => {
  const [loading, setLoading] = useState<boolean>(true);
  const [open, setOpen] = useState<boolean>(false);
  const [response, setResponse] = useState<IReplacementsResponse>({
    error: null,
    data: [],
  });
  const [skeletonColumns] = useState(['Pattern', 'Replacement', 'Category', 'Order']);
  const [skeletonRows] = useState([1, 2, 3, 4, 5, 6, 7, 8, 9, 10]);
  const [replacement, setReplacement] = useState<ReplacementType>(new ReplacementTypeObject());

  const userInfo = useSelector((state: RootState) => state.userInfo.userInfo) as UserInfoType;
  const permissions = userInfo?.permissions;
  const replacements = response.data;

  const loadData = async () => {
    setLoading(true);
    try {
      const replacements = await getReplacements();
      if (replacements) {
        setResponse({
          data: replacements.map((replacementItem: any, index: number) => {
            return { ...replacementItem, replacementId: index };
          }),
          error: null,
        });
      }
    } catch (error) {
      console.error(error);
      setResponse({ data: [], error: 'Error loading data' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const deleteReplacementRow = async () => {
    const replacementsUpdate = [...replacements];
    const index = replacementsUpdate.findIndex((item) => item.replacementId === replacement.replacementId);
    const result = await deleteReplacement(replacement.pattern);
    if (result!=null && result.error) {
      alert(result.error);
    }
    else {
      replacementsUpdate.splice(index, 1)
      setResponse({
        error: null,
        data: replacementsUpdate,
      });
    }
    setOpen(false);
  }

  const updateReplacementRow = async () => {
    const replacementsUpdate = [...replacements];
    const index = replacementsUpdate.findIndex((item) => item.replacementId === replacement.replacementId);

    const result = await createUpdateReplacement(replacements[index], replacement);
    
    if (result.error) {
      alert(result.error);
    }
    else {
      replacementsUpdate[index] = replacement;

      setResponse({
        error: null,
        data: replacementsUpdate,
      });
    }


    setOpen(false);
  };

  const onRowClick = (data: ReplacementType) => {
    if(permissions?.includes('replacements.edit') || userInfo?.isAdmin){
      setOpen(true);
      setReplacement({
      ...data
    });
    }
  }

  const updatePattern = (e: SyntheticEvent) => {
    const pattern = (e.target as HTMLInputElement).value;
    setReplacement({
      ...replacement,
      pattern,
    });
  };

  const updateReplacement = (e: SyntheticEvent) => {
    const word = (e.target as HTMLInputElement).value;
    setReplacement({
      ...replacement,
      replacement: word,
    });
  };

  const updateCategory = (e: SyntheticEvent) => {
    const category = (e.target as HTMLInputElement).value;
    setReplacement({
      ...replacement,
      category,
    });
  };

  const updateOrder = (e: SyntheticEvent) => {
    const order = parseInt((e.target as HTMLInputElement).value, 10);
    setReplacement({
      ...replacement,
      order,
    });
  };

  const updateSample = (e: SyntheticEvent) => {
    const sample = (e.target as HTMLInputElement).value;
    setReplacement({
      ...replacement,
      sample,
    });
  };

  const updateComments = (e: SyntheticEvent) => {
    const comments = (e.target as HTMLInputElement).value;
    setReplacement({
      ...replacement,
      comments,
    });
  };

  if (response.error) {
    return (
      <div className="error">
        <span>{response.error}</span>
      </div>
    );
  }

  return (
    <>
      <Content loading={false}>
        <Button className="refresh-button" variant="primary" onClick={() => loadData()}>
          <FontAwesomeIcon icon={faSyncAlt} />
        </Button>
        {loading ? 
          <Skeleton columns={skeletonColumns} rows={skeletonRows} /> : 
          <ReplacementsTable 
            replacements={replacements}
            onRowClick={onRowClick}
           />}
        <Modal
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box className="modal-wrapper">
          {open && (
            <>
              <ModalHeader title={replacement?.pattern} onClose={() => setOpen(false)} />
              <ModalBody>
                <ReplacementForm
                  replacement={replacement}
                  updateReplacementRow={updateReplacementRow}
                  deleteReplacementRow={deleteReplacementRow}
                  updatePattern={updatePattern}
                  updateReplacement={updateReplacement}
                  updateCategory={updateCategory}
                  updateOrder={updateOrder}
                  updateSample={updateSample}
                  updateComments={updateComments}
                  permissions = {permissions}
                  userInfo={userInfo}
                />
              </ModalBody>
            </>
          )}
        </Box>
      </Modal>

      </Content>
    </>
  );
};

export default Replacements;
